import { useEffect, useState } from 'react';
import { CalendarDays, Ship, Search } from 'lucide-react';
import { API_BASE } from '@/lib/api-base';
import { useStorefront } from '@/context/StorefrontContext';

interface SailingScheduleItem {
  serviceCode: string | null;
  carrierName: string;
  vesselName: string;
  voyageNo: string | null;
  originPort: string;
  destinationPort: string;
  viaPort: string | null;
  etd: string | null;
  eta: string | null;
  cutoffAt: string | null;
  transitDays: number | null;
  remarks: string | null;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function SailingSchedulePage() {
  const { host, brand } = useStorefront();
  const [origin, setOrigin] = useState('Singapore');
  const [destination, setDestination] = useState('');
  const [carrier, setCarrier] = useState('');
  const [items, setItems] = useState<SailingScheduleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (nextOrigin = origin, nextDestination = destination, nextCarrier = carrier) => {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        host,
        origin: nextOrigin,
      });
      if (nextDestination.trim()) query.set('destination', nextDestination.trim());
      if (nextCarrier.trim()) query.set('carrier', nextCarrier.trim());

      const res = await fetch(`${API_BASE}/api/storefront/modules/sailing-schedule/search?${query.toString()}`);
      const body = await res.json() as {
        ok: boolean;
        data?: { items?: SailingScheduleItem[] };
        error?: { message?: string };
      };
      if (!res.ok || !body.ok) throw new Error(body.error?.message ?? 'Unable to load sailing schedules');
      setItems(Array.isArray(body.data?.items) ? body.data.items : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load sailing schedules');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load('Singapore', '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 animate-fade-in">
      <div className="flex items-start gap-4 mb-8">
        <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
          <CalendarDays className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Sailing Schedule</h1>
          <p className="text-slate-500 mt-1">
            Search upcoming sailings published by {brand.brandShortName}.
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
        className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm mb-8"
      >
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Origin</label>
            <input
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm focus:outline-none focus:border-brand-400"
              placeholder="Singapore"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Destination</label>
            <input
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm focus:outline-none focus:border-brand-400"
              placeholder="Manila"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Carrier</label>
            <input
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm focus:outline-none focus:border-brand-400"
              placeholder="Wan Hai"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand-700 px-4 py-3 text-sm font-medium text-white hover:bg-brand-800 transition-colors"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
          </div>
        </div>
      </form>

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
          Loading schedules...
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <Ship className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">No sailings found</h2>
          <p className="text-sm text-slate-500">Try a broader origin or destination search.</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="grid gap-4">
          {items.map((item) => (
            <div key={`${item.serviceCode ?? 'svc'}-${item.vesselName}-${item.etd ?? 'na'}`} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs font-semibold text-ocean-600 uppercase tracking-wide mb-2">
                    {item.serviceCode ?? 'Published Sailing'}
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">
                    {item.originPort} to {item.destinationPort}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {item.carrierName} · {item.vesselName}
                    {item.voyageNo ? ` / ${item.voyageNo}` : ''}
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Transit {item.transitDays ?? '—'} days
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4 mt-5 text-sm">
                <div>
                  <p className="text-xs text-slate-400 mb-1">ETD</p>
                  <p className="font-medium text-slate-900">{formatDate(item.etd)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">ETA</p>
                  <p className="font-medium text-slate-900">{formatDate(item.eta)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Cut-off</p>
                  <p className="font-medium text-slate-900">{formatDate(item.cutoffAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Via</p>
                  <p className="font-medium text-slate-900">{item.viaPort || 'Direct'}</p>
                </div>
              </div>

              {item.remarks && (
                <p className="mt-4 text-sm text-slate-500">{item.remarks}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
